import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { DealsService } from '@/data/deals.service';
import { LeadsService } from '@/data/leads.service';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { handleError } from '@/lib/errorHandler';

export interface Deal {
  id: string;
  workspace_id: string;
  lead_id: string;
  title: string | null;
  value: number | null;
  currency: string;
  probability: number;
  stage: 'new' | 'qualified' | 'proposal_sent' | 'negotiation' | 'won' | 'lost';
  order_status: 'none' | 'quote_sent' | 'confirmed' | 'paid' | 'in_delivery' | 'delivered' | 'post_sale';
  expected_close_date: string | null;
  delivery_date: string | null;
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
  loss_reason?: string | null;
  loss_notes?: string | null;
  lead?: {
    id: string;
    name: string;
    temperature: string | null;
    company?: string;
    phone?: string;
    next_action_at?: string;
    last_interaction_at?: string;
  };
}

export function useDeals() {
  const { scope, isLoading } = useWorkspaceScope();

  // CRITICAL: Make query key deterministic for proper cache isolation per workspace
  // Use scope.type and workspaceIds/workspaceId instead of scope object
  const queryKey = scope.type === 'single'
    ? ['deals', 'single', scope.workspaceId]
    : ['deals', 'all', scope.workspaceIds.sort().join(',')];

  return useQuery({
    queryKey,
    queryFn: async () => {
      // CRITICAL: Validate scope before querying
      if (scope.type === 'all') {
        if (scope.workspaceIds.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useDeals] scope.workspaceIds is empty in all mode - this should not happen');
          }
          return [];
        }
      } else if (scope.type === 'single') {
        if (!scope.workspaceId) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useDeals] scope.workspaceId is null in single mode - this should not happen');
          }
          return [];
        }
      }

      try {
        const dealsData = await DealsService.getDeals(scope);
        
        if (!dealsData || dealsData.length === 0) {
          return [];
        }

        const leadIds = dealsData.map(d => d.lead_id).filter(Boolean) as string[];
        
        let leadsMap = new Map<string, any>();
        
        if (leadIds.length > 0) {
          const leadsData = await LeadsService.getLeadsByIds(scope, leadIds);
          if (leadsData) {
            leadsData.forEach(lead => {
              leadsMap.set(lead.id, lead);
            });
          }
        }

        const result = (dealsData || []).map(d => ({
          ...d,
          probability: d.probability ?? 50,
          lead: leadsMap.get(d.lead_id) || undefined,
        })) as Deal[];
        
        // Debug logging (only in dev, only on success)
        if (process.env.NODE_ENV === 'development') {
          console.log('[useDeals] Fetched deals:', { count: result.length, scope });
        }
        
        return result;
      } catch (error) {
        logger.error('[useDeals] Error fetching deals', error, { scope });
        throw error;
      }
    },
    enabled: !isLoading && (scope.type === 'all' ? scope.workspaceIds.length > 0 : !!scope.workspaceId),
    staleTime: 30000,
  });
}

export function useUpdateDealProbability() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ dealId, probability }: { dealId: string; probability: number }) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      logger.mutation('updateDealProbability', { workspaceId: activeWorkspaceId, userId: user?.id, dealId, probability });
      return DealsService.updateDeal({ id: dealId, workspace_id: activeWorkspaceId, probability });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics'] });
    },
    onError: (error) => {
      handleError(error, { action: 'Update Deal Probability', component: 'useUpdateDealProbability', workspaceId });
    },
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (deal: Partial<Deal> & { lead_id: string }) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      logger.mutation('createDeal', { workspaceId: activeWorkspaceId, userId: user?.id, leadId: deal.lead_id });
      return DealsService.createDeal({
        workspace_id: activeWorkspaceId,
        lead_id: deal.lead_id,
        title: deal.title,
        value: deal.value,
        currency: deal.currency || 'USD',
        stage: deal.stage || 'new',
        expected_close_date: deal.expected_close_date,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
    onError: (error) => {
      handleError(error, { action: 'Create Deal', component: 'useCreateDeal', workspaceId });
    },
  });
}

export function useUpdateDealStage() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      dealId, 
      stage, 
      outcome 
    }: { 
      dealId: string; 
      stage: Deal['stage'];
      outcome?: string;
    }) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      logger.mutation('updateDealStage', { workspaceId: activeWorkspaceId, userId: user?.id, dealId, stage });
      const updatedDeal = await DealsService.updateDeal({ id: dealId, workspace_id: activeWorkspaceId, stage });

      // Delete notifications if deal is won/lost
      if ((stage === 'won' || stage === 'lost') && activeWorkspaceId) {
        try {
          const { deleteDealNotifications } = await import('@/lib/dealNotifications');
          await deleteDealNotifications(dealId, activeWorkspaceId);
        } catch (error) {
          // Non-blocking
          console.warn('[useUpdateDealStage] Failed to delete notifications', error);
        }
      }

      // Log stage change event
      if (activeWorkspaceId && user?.id && updatedDeal?.lead_id) {
        const { supabase } = await import('@/integrations/supabase/client');
        await supabase.from('action_events').insert([{
          workspace_id: activeWorkspaceId,
          user_id: user.id,
          lead_id: updatedDeal.lead_id,
          action_type: 'stage_change',
          outcome: (stage === 'won' || stage === 'lost') ? (outcome as any) : null,
          meta: { deal_id: dealId, new_stage: stage },
        }]);
      }

      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      handleError(error, { action: 'Update Deal Stage', component: 'useUpdateDealStage', workspaceId });
    },
  });
}

export function useUpdateDealOrderStatus() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      dealId, 
      orderStatus,
      deliveryDate,
    }: { 
      dealId: string; 
      orderStatus: Deal['order_status'];
      deliveryDate?: string;
    }) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      logger.mutation('updateDealOrderStatus', { workspaceId: activeWorkspaceId, userId: user?.id, dealId, orderStatus });
      
      const updates: any = { order_status: orderStatus };
      if (deliveryDate) updates.delivery_date = deliveryDate;

      const updatedDeal = await DealsService.updateDeal({ 
        id: dealId, 
        workspace_id: activeWorkspaceId, 
        ...updates 
      });

      // Log order status change
      if (activeWorkspaceId && user?.id && updatedDeal.lead_id) {
        const { supabase } = await import('@/integrations/supabase/client');
        const actionTypeMap: Record<string, string> = {
          quote_sent: 'quote_sent',
          confirmed: 'order_confirmed',
          paid: 'payment_received',
          delivered: 'delivered',
        };
        const actionType = actionTypeMap[orderStatus];
        if (actionType) {
          await supabase.from('action_events').insert([{
            workspace_id: activeWorkspaceId,
            user_id: user.id,
            lead_id: updatedDeal.lead_id,
            action_type: actionType as any,
            meta: { deal_id: dealId, order_status: orderStatus },
          }]);
        }
      }

      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error) => {
      handleError(error, { action: 'Update Deal Order Status', component: 'useUpdateDealOrderStatus', workspaceId });
    },
  });
}
